// Native browser popup notifications (Notification API) — shows even when
// this tab isn't the focused/active one, as long as it's still open.
// Does NOT work once the browser itself is fully closed; that needs a
// service worker + Web Push subscription, a separate feature.

// Ask once, the first time this is used (must be from a user gesture or at
// least a page that's already loaded — browsers block silent auto-prompts
// on some flows, but this is the standard pattern used everywhere).
// Resolves true once permission is actually granted (already-granted or
// just-granted), false otherwise — callers use this to know when it's safe
// to go on and subscribe to push.
export async function requestNotificationPermission() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}

// title/body: plain text shown in the OS popup.
// onClick: optional — runs when the user clicks the popup (e.g. focus the tab).
export function showBrowserNotification(title, body, onClick) {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;

  // Don't pop a notification for something the user is already looking at —
  // only when this tab is hidden/unfocused.
  if (document.visibilityState === "visible" && document.hasFocus()) return;

  const n = new Notification(title, {
    body,
    icon: "/favicon.png",
    tag: "b2bcabs-chat", // replaces any previous popup instead of stacking endlessly
  });

  n.onclick = () => {
    window.focus();
    if (onClick) onClick();
    n.close();
  };
}

// Public VAPID key — safe to ship in frontend code, it is not a secret
// (the matching private key stays server-side only, in the socket server's
// .env). Read from .env here too so both sides are changed from one place.
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

// Registers the service worker and subscribes this browser to Web Push, so
// notifications can still arrive even with the browser fully closed (the
// browser's own push service wakes the service worker up). saveFn is called
// with the subscription object to persist it server-side — pass
// ChatServices.savePushSubscription.
export async function setupPushNotifications(saveFn) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  if (Notification.permission !== "granted") return;
  if (!VAPID_PUBLIC_KEY) {
    console.error("Push not set up: REACT_APP_VAPID_PUBLIC_KEY is missing from .env");
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register("/serviceworker.js");
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }
    if (saveFn) await saveFn(subscription.toJSON());
  } catch (err) {
    console.error("Push subscription failed:", err);
  }
}

// Called on logout — unsubscribes this browser from Web Push and tells the
// server to drop the saved subscription. Without this, the subscription
// (and the push_subscriptions row) stay alive after logout, so the service
// worker keeps showing pushes for an account nobody's logged into on this
// device anymore. deleteFn is called with the endpoint to remove — pass
// ChatServices.deletePushSubscription. Must run BEFORE the auth token is
// cleared, since deleteFn needs it to identify whose subscription this is.
export async function teardownPushNotifications(deleteFn) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    if (deleteFn) await deleteFn(subscription.endpoint);
    await subscription.unsubscribe();
  } catch (err) {
    console.error("Push unsubscribe failed:", err);
  }
}
